import axios from "axios";
import { newLogger } from "./util";
import LoadingMixin from "@/mixins/LoadingMixin";
import pinia from "@/store/pinia";
import { useAuthSessionStore } from "@/store/pinia/useAuthSessionStore";

let logger = newLogger("Network");

let VERSION_HEADER = "X-Version";

class Network {
  constructor() {
    this.router = null;
    this.store = null;
    this.http = null;
    this.excelNetwork = null;
    this.pdfNetwork = null;
  }

  /**
   * Called from app.vue on creation, sets the store reference and creates the axios instance
   */
  configure(store, router, toast) {
    this.router = router;
    this.store = store;
    this.http = this.createAxiosInstance();
    this.excelNetwork = this.createExcelExportNetwork();
    this.pdfNetwork = this.createPdfExportNetwork();
    this.toast = toast;
  }

  getJwt() {
    return "";
  }

  getVersion() {
    const env = this.store.getters["env/getField"];
    return env("CLIENT_VERSION");
  }

  createUnauthorizedAxiosInstance() {
    return axios.create({
      baseURL: "/api",
      timeout: 30000,
      withCredentials: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      // responseType: 'json',
    });
  }

  createAxiosInstance(silenceToast = false) {
    const http = axios.create({
      baseURL: "/api",
      timeout: 60000,
      withCredentials: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      // responseType: 'json',
    });
    http.interceptors.request.use(
      (config) => {
        config.headers[VERSION_HEADER] = this.getVersion();
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error!");
        logger.warn(error);
        if (error.response.status === 401) {
          // unauthorized
          useAuthSessionStore(pinia).boot(true);
          if (
            this.router.currentRoute.name !== "SignIn" &&
            this.router.currentRoute.name !== "SignInToTenant"
          )
            this.router.push({
              path: "/login",
              query: { returnUrl: this.router.currentRoute.fullPath },
            });
          LoadingMixin.methods.doFinishLoading().then(() => {});
        } else {
          if (!silenceToast) {
            this.toast.add({
              severity: "error",
              summary: "Something went wrong.., please try again later.",
              life: 3000,
            });
          }
          if (!error.response) {
            logger.warn("No error.response in response, rejecting.");
            return Promise.reject(error);
          }
        }
        // if (error.response.status === 503) { // FIXME:: this is super unsafe
        //   window.location.reload();
        // }
        throw error;
      }
    );
    return http;
  }

  createExcelExportNetwork() {
    const http = axios.create({
      baseURL: "/api",
      withCredentials: true,
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      responseType: "blob",
    });
    http.interceptors.request.use(
      (config) => {
        config.headers[VERSION_HEADER] = this.getVersion();
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error response.");
        logger.warn(error);
        if (error.response.status === 401) {
          useAuthSessionStore(pinia).boot(true);
          this.router.push("/login");
          LoadingMixin.methods.doFinishLoading().then(() => {});
        }
        if (error.response.status === 403) {
          this.router.push("/");
        }
        if (error.response.status === 503) {
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
    return http;
  }

  createPdfExportNetwork() {
    const http = axios.create({
      baseURL: "/api",
      withCredentials: true,
      headers: {
        Accept: "application/pdf",
      },
      responseType: "blob",
    });
    http.interceptors.request.use(
      (config) => {
        config.headers[VERSION_HEADER] = this.getVersion();
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error response.");
        logger.warn(error);
        if (error.response.status === 401) {
          useAuthSessionStore(pinia).boot(true);
          this.router.push("/login");
          LoadingMixin.methods.doFinishLoading().then(() => {});
        }
        if (error.response.status === 403) {
          this.router.push("/");
        }
        if (error.response.status === 503) {
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
    return http;
  }

  async connection() {
    const authStore = useAuthSessionStore(pinia);
    if (!authStore.isReady && !authStore.bootstrapped) {
      await authStore.bootstrap();
    }
    return this.http;
  }

  async excelConnection() {
    const authStore = useAuthSessionStore(pinia);
    if (!authStore.isReady && !authStore.bootstrapped) {
      await authStore.bootstrap();
    }
    return this.excelNetwork;
  }

  async pdfConnection() {
    const authStore = useAuthSessionStore(pinia);
    if (!authStore.isReady && !authStore.bootstrapped) {
      await authStore.bootstrap();
    }
    return this.pdfNetwork;
  }

  checkRefresh(response) {
    const refreshHeader = response.headers["refresh"];

    if (refreshHeader) {
      const refreshTime = parseInt(refreshHeader, 10);

      if (!isNaN(refreshTime)) {
        console.log(
          `Received Refresh: ${refreshTime}, reloading page in ${refreshTime} seconds...`
        );

        setTimeout(() => {
          window.location.reload();
        }, refreshTime * 1000);
      }
    }
  }
}

// singleton
export default new Network();
